import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Container, Table, Badge } from "reactstrap";
import { getUserLogs } from "../../services/api/logsService.ts";

const ChapterLogs = () => {
    const { userId, chapterName } = useParams();
    const [userInfo, setUserInfo] = useState({ name: '', surname: '' });
    const [logs, setLogs] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLogContent, setSelectedLogContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId || !chapterName) {
            setLoading(false);
            return;
        }

        const fetchLogs = async () => {
            try {
                setLoading(true);
                setError(null);

                const res = await getUserLogs(parseInt(userId));

                if (!res || !Array.isArray(res)) {
                    console.error("Invalid response format:", res);
                    setError("Format de données invalide");
                    setLogs([]);
                    return;
                }

                const decodedChapterName = decodeURIComponent(chapterName);
                const filtered = res.filter(log => log.logs_CHAPTER === decodedChapterName);
                setLogs(filtered);
            } catch (err) {
                console.error("Error fetching logs:", err);
                setError("Erreur lors du chargement des logs");
                setLogs([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
        // Récupérer la liste des utilisateurs et trouver le bon
        fetch('/api/user/list')
            .then(res => res.json())
            .then(users => {
                if (!Array.isArray(users)) return;
                const user = users.find(u => String(u.user_ID) === String(userId));
                if (user) {
                    setUserInfo({ name: user.user_NAME, surname: user.user_SURNAME });
                }
            });

    }, [userId, chapterName]);

    const toggleModal = (logContent = null) => {
        if (logContent !== null) {
            setSelectedLogContent(logContent);
            setModalOpen(true);
        } else {
            setModalOpen(false);
            setTimeout(() => setSelectedLogContent(null), 200);
        }
    };

    const parseLogContent = (content) => {
        try {
            if (typeof content === 'object' && content !== null) {
                return content;
            }

            let parsed = JSON.parse(content);

            while (typeof parsed === 'string') {
                parsed = JSON.parse(parsed);
            }

            return parsed;
        } catch (err) {
            console.error("❌ Error parsing log content:", err);
            return null;
        }
    };

    const getScoreBadgeColor = (score) => {
        if (score >= 80) return "success";
        if (score >= 50) return "warning";
        return "danger";
    };

    if (loading) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                    </div>
                </Container>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-content">
                <Container fluid>
                    <div className="alert alert-danger">{error}</div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid>
                <div className="mb-4 text-center">
                    <h1 className="mb-1 text-primary"><i className="mdi mdi-school-outline"></i> {decodeURIComponent(chapterName)}</h1>
                    <h2 className="text-tertiary">{userInfo.name} {userInfo.surname}</h2>
                    <p className="text-muted mb-2 text-center">Utilisateur #{userId} : {logs.length} cours</p>
                </div>

                {logs.length === 0 ? (
                    <div className="text-center py-5">
                        <i className="mdi mdi-information-outline" style={{ fontSize: '48px', color: '#ccc' }}></i>
                        <p className="text-muted mt-3">Aucun log disponible pour ce chapitre.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '5%' }}>ID</th>
                                    <th style={{ width: '25%' }}>Date & Heure</th>
                                    <th style={{ width: '45%' }}>Titre du Cours</th>
                                    <th style={{ width: '10%' }}>Temps</th>
                                    <th style={{ width: '10%' }}>Score</th>
                                    <th style={{ width: '5%' }} className="text-center">Détails</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log, index) => (
                                    <tr key={log.logs_ID || index}>
                                        {/* ID */}
                                        <td>
                                            <code>#{log.logs_ID}</code>
                                        </td>
                                        {/* Date & Heure */}
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <i className="mdi mdi-clock-outline me-2 text-muted"></i>

                                                {new Date(log.logs_TIMESTAMP * 1000).toLocaleString('fr-FR', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                        </td>
                                        {/* Titre du cours */}
                                        <td>{log.logs_COURSE_TITLE}</td>
                                        {/* Temps */}
                                        <td>{log.logs_TIMER}</td>
                                        {/* Score badge */}
                                        <td>
                                            <Badge
                                                color={getScoreBadgeColor(log.logs_SCORE)}
                                                className="px-3 py-2"
                                                style={{ fontSize: '14px' }}
                                            >
                                                {log.logs_SCORE}%
                                            </Badge>
                                        </td>
                                        {/* 🔎 Détails */}
                                        <td className="text-center">
                                            <Button
                                                color="outline-primary"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => toggleModal(log.logs_CONTENT)}
                                                title="Voir le détail"
                                            >
                                                <i className="mdi mdi-magnify"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal rendu UNIQUEMENT si modalOpen est true */}
                {modalOpen && (
                    <Modal
                        isOpen={true}
                        toggle={() => toggleModal()}
                        size="lg"
                        unmountOnClose={true}
                        fade={true}
                    >
                        <ModalHeader toggle={() => toggleModal()}>Contenu du log</ModalHeader>
                        <ModalBody>
                            {selectedLogContent ? (
                                (() => {
                                    const parsedContent = parseLogContent(selectedLogContent);
                                    return parsedContent && Array.isArray(parsedContent) ? (
                                        //<h1>{duration}</h1>
                                        parsedContent.map((topic, idx) => (
                                            <div key={idx} style={{ marginBottom: 20 }}>
                                                <h5>📌 {topic.topicTitle}</h5>
                                                {topic.quizzes?.map((quiz, qIdx) => (
                                                    <div key={qIdx} style={{ marginLeft: 20, marginBottom: 10 }}>
                                                        <p><strong>Q:</strong> {quiz.quizQuestion}</p>
                                                        <p><strong>Réussite :</strong> {quiz.isSucceed ? "✅" : "❌"}</p>
                                                        <ul>
                                                            {quiz.quizChoices?.map(choice => (
                                                                <li key={choice.choiceID}>
                                                                    {choice.choice} {choice.correct ? "(✔)" : ""} {choice.isSelected ? "(sélectionné)" : ""}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        ))
                                    ) : (
                                        <p>Format de contenu invalide</p>
                                    );
                                })()
                            ) : (
                                <p>Aucun contenu disponible</p>
                            )}
                        </ModalBody>
                        <ModalFooter>
                            <Button color="secondary" onClick={() => toggleModal()}>
                                Fermer
                            </Button>
                        </ModalFooter>
                    </Modal>

                )}
                <div className="mb-3 mt-4 d-flex justify-content-center">
                    <button
                        title="Retour à la liste des utilisateurs"
                        className="btn btn-outline-primary" onClick={() => window.history.back()}>
                        <i className="mdi mdi-backspace-outline fs-4 py-0 align-middle"></i> Retour au dashboard administrateur
                    </button>
                </div>
            </Container>
        </div>
    );
};

export default ChapterLogs;

//Creators : Florian Cohen-Joly  @3DDGAMES -- Under license
